import React, { useEffect, useMemo, useState } from "react";
import useResponsiveFlags from "../../hooks/useResponsiveFlags";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Box,
  Typography,
  Alert,
  Grid2,
} from "@mui/material";
import axios from "../../api/axios";
import FilterMenuToken from "./FilterMenuToken";
import TokenHeadFiles from "./TokenTableHead";
import WrapperContainer from "../../components/utilities/WrapperContainer";

const TokenTable = () => {
  const { isDesktop, isSmallMobileScreen } = useResponsiveFlags();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("orgname");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [filters, setFilters] = useState({
    tokenType: null,
    startDate: null,
    endDate: null,
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [organizations, setOrganizations] = useState([]);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [showModifyForm, setShowModifyForm] = useState(false);
  const [tokenActionLoading, setTokenActionLoading] = useState(false);

  const [tokenForm, setTokenForm] = useState({
    orgid: "",
    type: "trial",
    sampletotal: "",
    daysUntilExpire: "",
  });

  const [modifyDialog, setModifyDialog] = useState({
    open: false,
    action: "",
    value: "",
  });

  const tokenTypes = [
    { value: "trial", label: "Fractions Trial" },
    { value: "full", label: "Fractions Full" },
    { value: "seat", label: "Fractions Seat" },
    { value: "hires-trial", label: "HiRes Trial" },
    { value: "hires-full", label: "HiRes Full" },
    { value: "hires-seat", label: "HiRes Seat" },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    if (!search) handleSearchButtonClick();
  }, [search]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchData = async () => {
    setDataLoading(true);
    setErrorMessage("");
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
    };
    if (order) params.sort_order = order;
    if (orderBy) params.sort_by = orderBy;
    if (filters.tokenType) params.tokenType = filters.tokenType;
    if (filters.startDate) params.date_from = filters.startDate;
    if (filters.endDate) params.date_to = filters.endDate;
    if (search) params.search = search;
    axios
      .get("/api/admin_dashboard/tokensdata", { params })
      .then((response) => {
        const result = response.data;
        setData(result.data || []);
        setTotalCount(result.total || 0);
        setDataLoading(false);
      })
      .catch((err) => {
        setErrorMessage("Failed to fetch tokens table data.");
        setDataLoading(false);
      });
  };

  const fetchOrganizations = async () => {
    axios
      .get("/api/admin_dashboard/gettokenorgs")
      .then((response) => {
        setOrganizations(response.data || []);
      })
      .catch((err) => {
        setErrorMessage("Failed to fetch Organizations for token creation form.");
      });
  };

  const handleCreateToken = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    // Trim & Normalize
    const orgid = Number(tokenForm.orgid);
    const type = tokenForm.type?.trim();
    const sampletotal = Number(tokenForm.sampletotal);
    const daysUntilExpire = Number(tokenForm.daysUntilExpire);

    // Required Field Check
    if (
      !orgid ||
      !type ||
      tokenForm.sampletotal === "" ||
      tokenForm.daysUntilExpire === ""
    ) {
      setErrorMessage("All fields are required.");
      return;
    }

    // Number Validation
    const sampleInvalid = !Number.isInteger(sampletotal) || sampletotal <= 0;

    const daysInvalid =
      !Number.isInteger(daysUntilExpire) || daysUntilExpire <= 0;

    if (sampleInvalid && daysInvalid) {
      setErrorMessage(
        "Number of samples and Days until expire must both be positive integers."
      );
      return;
    }

    if (type != "seat" && type != "hires-seat"){
      if (sampleInvalid) {
        setErrorMessage("Number of samples must be a positive integer.");
        return;
      }
    } else {
      if (sampletotal != 0) {
        setErrorMessage("Number of samples must be 0 for a seat based token.");
        return;
      }
    }

    if (daysInvalid) {
      setErrorMessage("Days until expire must be a positive integer.");
      return;
    }
    if (sampletotal > 100000) {
      setErrorMessage("Sample total is too large.");
      return;
    }

    if (daysUntilExpire > 3650) {
      setErrorMessage("Days until expire is too large.");
      return;
    }

    if (tokenActionLoading) return;
    setTokenActionLoading(true);

    axios
      .post("/api/admin_dashboard/createtoken", {
        orgid,
        type,
        sampletotal,
        daysUntilExpire,
      })
      .then((response) => {
        if (response.data.success) {
          setSuccessMessage(response.data.message);
          setTokenForm({
            orgid: "",
            type: "full",
            sampletotal: "",
            daysUntilExpire: "",
          });
          fetchData();
          setTimeout(() => setSuccessMessage(""), 5000);
          setTokenActionLoading(false);
        } else {
          setErrorMessage(response.data.message);
          setTokenActionLoading(false);
        }
      })
      .catch((err) => {
        setErrorMessage("Failed to create the token due to internal error");
        setTokenActionLoading(false);
      });
  };

  const handleModifyToken = async (action) => {
    const selectedRowData = data.find((token) => token.id === selectedRow);

    // Check if token is locked for lock action or unlocked for unlock action
    if (action === "lock_token" && selectedRowData.locked) {
      setErrorMessage("This token is already locked.");
      return;
    }

    if (action === "unlock_token" && !selectedRowData.locked) {
      setErrorMessage("This token is already unlocked.");
      return;
    }

    // Check if token is provisional
    if (selectedRowData.provisional) {
      setErrorMessage(
        "Cannot modify tokens attached to provisional organizations."
      );
      return;
    }

    if (
      ["add_time", "subtract_time", "add_sample", "subtract_sample"].includes(
        action
      )
    ) {
      setModifyDialog({ open: true, action, value: "" });
    } else {
      const actionLabels = {
        lock_token: "Lock Token",
        unlock_token: "Unlock Token",
        rollback_pending: "Rollback Pending Samples",
        commit_pending: "Commit Pending Samples",
      };

      openConfirmationDialog(
        actionLabels[action],
        `Are you sure you want to ${actionLabels[action].toLowerCase()}?`,
        () => executeModification(action, null, selectedRowData.id)
      );
    }
  };

  const executeModification = async (action, value, token) => {
      setErrorMessage("");
      setSuccessMessage("");
      if(tokenActionLoading) return;
      setTokenActionLoading(true);
      
      const payload = {action, token};
      if (value !== null && value !== undefined) {
        payload.value = parseInt(value, 10);
      }

      axios.post('/api/admin_dashboard/modifytoken', payload)
      .then((response)=>{
        if(response.data.success){
          setSuccessMessage(response.data.message);
          fetchData();
          setSelectedRow(null);
          setTimeout(() => setSuccessMessage(""), 5000);
          setTokenActionLoading(false);
        } else {
          setErrorMessage(response.data.message);
          setTokenActionLoading(false);
        }
      }) .catch((err)=>{
        setErrorMessage("Failed to modify the token due to internal error");
        setTokenActionLoading(false);
      })
      setModifyDialog({ open: false, action: "", value: "" });
      closeConfirmationDialog();
  };

  const handleSelectRow = (tokenId) => {
    setSelectedRow((prev) => (prev === tokenId ? null : tokenId));
  };

  const openConfirmationDialog = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const closeConfirmationDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [column]: prevFilters[column] === value ? null : value,
    }));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value.toLowerCase());
  };

  const handleSearchButtonClick = () => {
    if (page != 0) setPage(0);
    else fetchData();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    return [...data].sort(getComparator(order, orderBy));
  }, [data, order, orderBy]);

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    if (
      orderBy === "created" ||
      orderBy === "expire" ||
      orderBy === "last_used"
    ) {
      valueA = valueA ? new Date(valueA) : null;
      valueB = valueB ? new Date(valueB) : null;
    }

    if (
      orderBy === "id" ||
      orderBy === "total_samples" ||
      orderBy === "remain_samples" ||
      orderBy === "pending_samples"
    ) {
      valueA = Number(valueA) || 0;
      valueB = Number(valueB) || 0;
    }

    if (typeof valueA === "string") valueA = valueA.toLowerCase();
    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA == null) return 1;
    if (valueB == null) return -1;

    if (valueB < valueA) return -1;
    if (valueB > valueA) return 1;
    return 0;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayName = days[date.getDay()];
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    return `${dayName} ${month}/${day}/${year}`;
  };

  // Export CSV code
  // const handleExport = () => {
  //   try {
  //     const params = new URLSearchParams();
  //     if (search) params.append("search", search);
  //     if (filters.startDate) params.append("startDate", filters.startDate);
  //     if (filters.endDate) params.append("endDate", filters.endDate);
  //     if (filters.orgid) params.append("orgid", filters.orgid);
  //     if (filters.tokenType) params.tokenType = filters.tokenType;

  //     window.open(
  //       `http://localhost:8000/api/admin_dashboard/tokens/export?${params}`,
  //       "_blank"
  //     );
  //   } catch (error) {
  //     console.error("Export error:", error);
  //     setErrorMessage("Failed to export data");
  //   }
  // };

  return (
    <>
      <WrapperContainer
        title="Tokens"
        loggedin={true}
        content={
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6" component="h2">
                    Create New Token
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setShowTokenForm(!showTokenForm)}
                    size="small"
                  >
                    {showTokenForm ? "Hide Form" : "Show Form"}
                  </Button>
                </Box>

                {showTokenForm && (
                  <Grid2 container spacing={2} sx={{mt:2}}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Organization *</InputLabel>
                        <Select
                          required
                          value={tokenForm.orgid}
                          onChange={(e) =>
                            setTokenForm({
                              ...tokenForm,
                              orgid: e.target.value,
                            })
                          }
                          label="Organization"
                        >
                          {organizations.map((org) => (
                            <MenuItem key={org.id} value={org.id}>
                              {org.id} - {org.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Token Type *</InputLabel>
                        <Select
                          required
                          value={tokenForm.type}
                          onChange={(e) =>
                            setTokenForm({ ...tokenForm, type: e.target.value })
                          }
                          label="Token Type"
                        >
                          {tokenTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        required
                        fullWidth
                        size="small"
                        label="Number of Samples."
                        type="number"
                        value={tokenForm.sampletotal}
                        onChange={(e) =>
                          setTokenForm({
                            ...tokenForm,
                            sampletotal: Number(e.target.value),
                          })
                        }
                        inputProps={{ min: 1 }}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        required
                        fullWidth
                        size="small"
                        label="Days Until Expire."
                        type="number"
                        value={tokenForm.daysUntilExpire}
                        onChange={(e) =>
                          setTokenForm({
                            ...tokenForm,
                            daysUntilExpire: Number(e.target.value),
                          })
                        }
                        inputProps={{ min: 1 }}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateToken}
                        fullWidth
                      >
                        Create Token
                      </Button>
                    </Grid2>
                  </Grid2>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="h6" component="h2">
                    Modify Selected Token
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setShowModifyForm(!showModifyForm)}
                    size="small"
                  >
                    {showModifyForm ? "Hide Form" : "Show Form"}
                  </Button>
                </Box>

                {showModifyForm && (
                  <Grid2 container spacing={1} sx={{mt:2}}>
                    <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleModifyToken("add_time")}
                        disabled={!selectedRow}
                        size="small"
                      >
                        Add Time
                      </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleModifyToken("subtract_time")}
                        disabled={!selectedRow}
                        size="small"
                      >
                        Subtract Time
                      </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleModifyToken("add_sample")}
                        disabled={!selectedRow}
                        size="small"
                      >
                        Add Sample
                      </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleModifyToken("subtract_sample")}
                        disabled={!selectedRow}
                        size="small"
                      >
                        Subtract Sample
                      </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleModifyToken("lock_token")}
                        disabled={!selectedRow}
                        size="small"
                      >
                        Lock Token
                      </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleModifyToken("unlock_token")}
                        disabled={!selectedRow}
                        size="small"
                      >
                        Unlock Token
                      </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleModifyToken("rollback_pending")}
                        // disabled={!selectedRow}
                        disabled
                        size="small"
                      >
                        Rollback Pending
                      </Button>
                    </Grid2>
                    <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => handleModifyToken("commit_pending")}
                        // disabled={!selectedRow}
                        disabled
                        size="small"
                      >
                        Commit Pending
                      </Button>
                    </Grid2>
                  </Grid2>
                )}
              </CardContent>
            </Card>

            <div className="d-flex flex-column-reverse flex-xl-row justify-content-between mb-xl-3">
              <div className="d-flex align-items-center">
                <input
                  type="text"
                  className="form-control search-icon"
                  placeholder="Search"
                  value={search}
                  onChange={handleSearchChange}
                />
                <Button
                  size="medium"
                  variant="contained"
                  onClick={handleSearchButtonClick}
                  sx={{ padding: "6px 0", marginLeft: "10px" }}
                >
                  Search
                </Button>
                <FilterMenuToken
                  handleFilterChange={handleFilterChange}
                  filters={filters}
                />
                {/* <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExport}
                  style={{ marginLeft: "12px" }}
                  disabled={totalCount === 0}
                >
                  Export CSV
                </Button> */}
              </div>
            </div>

            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage("")}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            )}

            {successMessage && (
              <Alert
                severity="success"
                onClose={() => setSuccessMessage("")}
                sx={{ mb: 2 }}
              >
                {successMessage}
              </Alert>
            )}

            <TableContainer
              className="table-scroll-visible"
              sx={{
                height: isDesktop
                  ? "calc(100vh - 420px)"
                  : "calc(100vh - 480px)",
                minHeight: isDesktop ? "400px" : "300px",
                overflowX: "auto",
                overflowY: "auto"
              }}
            >
              <Table
                stickyHeader
                sx={{ minWidth: 750 }}
                aria-label="token table"
              >
                <TokenHeadFiles
                  order={order}
                  orderBy={orderBy}
                  onSort={handleSort}
                />
                <TableBody>
                  {dataLoading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : sortedData.length > 0 ? (
                    sortedData.map((row) => (
                      <TableRow
                        key={row.id}
                        style={{
                          backgroundColor: row.locked ? "#aaacb5" : "#fff",
                        }}
                      >
                        <TableCell
                          style={
                            !isSmallMobileScreen
                              ? {
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 10,
                                  backgroundColor: row.locked
                                    ? "#aaacb5"
                                    : "#fff",
                                }
                              : {}
                          }
                        >
                          <input
                            type="checkbox"
                            checked={selectedRow === row.id}
                            onChange={() => handleSelectRow(row.id)}
                          />
                        </TableCell>
                        <TableCell
                          style={
                            !isSmallMobileScreen
                              ? {
                                  position: "sticky",
                                  top: 0,
                                  left: 45,
                                  zIndex: 10,
                                  backgroundColor: row.locked
                                    ? "#aaacb5"
                                    : "#fff",
                                }
                              : {}
                          }
                          className="custom-size"
                        >
                          {row.token}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.orgname || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.type || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.total_samples || 0}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.remain_samples || 0}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.pending_samples || 0}
                        </TableCell>
                        <TableCell className="custom-size">
                          {formatDate(row.expire)}
                        </TableCell>
                        <TableCell className="custom-size">
                          {formatDate(row.last_used)}
                        </TableCell>
                        <TableCell className="custom-size">
                          {formatDate(row.created)}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.locked ? "Yes" : "No"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        {search || filters.startDate || filters.endDate
                          ? "No matching tokens found."
                          : "No tokens found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {!dataLoading && totalCount > 0 && (
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count} total tokens`
                }
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    alignItems: "center",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    margin: 0,
                  },
                  "& .MuiTablePagination-displayedRows": {
                    margin: 0,
                  },
                }}
              />
            )}

            <Dialog
              open={modifyDialog.open}
              onClose={() =>
                setModifyDialog({ open: false, action: "", value: "" })
              }
            >
              <DialogTitle>
                {modifyDialog.action.replace(/_/g, " ").toUpperCase()}
              </DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label={
                    modifyDialog.action.includes("time") ? "Days" : "Samples"
                  }
                  type="number"
                  fullWidth
                  value={modifyDialog.value}
                  onChange={(e) =>
                    setModifyDialog({ ...modifyDialog, value: e.target.value })
                  }
                  inputProps={{ min: 1 }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() =>
                    setModifyDialog({ open: false, action: "", value: "" })
                  }
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const tokenData = data.find(
                      (token) => token.id === selectedRow
                    );
                    if (!modifyDialog.value || modifyDialog.value <= 0) {
                      setErrorMessage("Please enter a valid positive number.");
                      return;
                    }
                    executeModification(
                      modifyDialog.action,
                      modifyDialog.value,
                      tokenData.id
                    );
                  }}
                  variant="contained"
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={confirmDialog.open} onClose={closeConfirmationDialog}>
              <DialogTitle>{confirmDialog.title}</DialogTitle>
              <DialogContent>
                <Typography>{confirmDialog.message}</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeConfirmationDialog}>Cancel</Button>
                <Button
                  onClick={() =>
                    confirmDialog.onConfirm && confirmDialog.onConfirm()
                  }
                  variant="contained"
                  color="primary"
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          </>
        }
      />
    </>
  );
};

export default TokenTable;
